/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useContext } from "react";
import { getAccessibleComponentToRender, getRelativeNodePosition } from "./AccessibilityComponentHelper";
import { FormattedCopyContext } from "utils/CopyTextUtils";

const AccessibleCellNode = ({ style, cell, zoomLevel, pageIndex }) => {
  if (!cell.children) return null;
  const formattedCopyContext = useContext(FormattedCopyContext);
  // If used in accessibility layer, no border is needed. For copy with formatting, make a default border.
  if (formattedCopyContext) style.border = "1px solid black";
  return (
    <td style={style}>
      {cell.children.map((child, childIndex) =>
        getAccessibleComponentToRender(child, zoomLevel, `cellchild${childIndex}${child.id}`, pageIndex, cell)
      )}
    </td>
  );
};

const AccessibleRowNode = ({ style, row, pageIndex, zoomLevel }) => {
  if (!row.children) return null;
  return (
    <tr style={style}>
      {row.children.map((child, index) => {
        const style = getRelativeNodePosition(child.contentRect, row.contentRect, pageIndex);
        return (
          <AccessibleCellNode style={style} key={index} cell={child} pageIndex={pageIndex} zoomLevel={zoomLevel} />
        );
      })}
    </tr>
  );
};

export const AccessibleTableNode = ({ style, table, pageIndex, zoomLevel }) => {
  if (!table.children) return null;
  return (
    <table role="table" style={{ ...style, borderCollapse: "collapse" }}>
      {table.children.map((child, index) => {
        const style = getRelativeNodePosition(child.contentRect, table.contentRect, pageIndex);
        return <AccessibleRowNode style={style} key={index} row={child} pageIndex={pageIndex} zoomLevel={zoomLevel} />;
      })}
    </table>
  );
};
